import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { anonymizerConfigKeys } from '@/entities/anonymizer-config';
import { API_TAGS } from '@/shared/api';

import { S1_ANONYMIZER_CODE } from '../constants';

import {
  fetchBitrixCalls,
  fetchBitrixTouchSends,
  fetchBitrixTouchSettings,
  fetchC1BotCandidates,
  fetchC1BotLoads,
  fetchC1ToAsrCalls,
  fetchC3AsrDemoLoads,
  fetchIntegration,
  fetchIntegrationRequestLog,
  fetchIntegrationScenarios,
  fetchIntegrationStatistics,
  fetchIntegrations,
  runIntegrationScenario,
  saveBitrixTouchSettings,
  startC1BotLoad,
  startC1ToAsrTranscription,
  startC3AsrDemoLoad,
  updateIntegration,
  updateIntegrationMockMode,
} from './api';
import type {
  BitrixCallListParams,
  BitrixTouchSendListParams,
  C1BotCandidateListParams,
  C1BotLoadListParams,
  C1ToAsrCallsParams,
  C3AsrDemoLoadListParams,
  IntegrationListParams,
  IntegrationMockModeRequest,
  IntegrationRequestLogParams,
  IntegrationScenarioRunRequest,
  IntegrationStatisticsParams,
  IntegrationUpdateRequest,
} from './types';

/**
 * Ключи кеша TanStack Query для интеграций
 */
export const integrationsKeys = {
  all: [API_TAGS.INTEGRATIONS] as const,
  lists: () => [...integrationsKeys.all, 'list'] as const,
  list: (params?: IntegrationListParams) => [...integrationsKeys.lists(), { ...params }] as const,
  details: () => [...integrationsKeys.all, 'detail'] as const,
  detail: (code: string) => [...integrationsKeys.details(), code] as const,
  bitrixCalls: (params?: BitrixCallListParams) =>
    [...integrationsKeys.all, 'c2-bitrix', 'calls', { ...params }] as const,
  bitrixTouchSettings: () => [...integrationsKeys.all, 'c2-bitrix', 'touch'] as const,
  bitrixTouchSends: (params?: BitrixTouchSendListParams) =>
    [...integrationsKeys.all, 'c2-bitrix', 'touch', 'sends', { ...params }] as const,
  statistics: (code: string, params?: IntegrationStatisticsParams) =>
    [...integrationsKeys.all, 'statistics', code, { ...params }] as const,
  requestLog: (code: string, params?: IntegrationRequestLogParams) =>
    [...integrationsKeys.all, 'request-log', code, { ...params }] as const,
  scenarios: (code: string) => [...integrationsKeys.all, 'scenarios', code] as const,
  c3AsrDemoLoads: (params?: C3AsrDemoLoadListParams) =>
    [...integrationsKeys.all, 'c3-asr-demo', 'loads', { ...params }] as const,
  c1BotCandidates: (params?: C1BotCandidateListParams) =>
    [...integrationsKeys.all, 'c1-bot', 'candidates', { ...params }] as const,
  c1BotLoads: (params?: C1BotLoadListParams) => [...integrationsKeys.all, 'c1-bot', 'loads', { ...params }] as const,
  c1ToAsrCalls: (params?: C1ToAsrCallsParams) =>
    [...integrationsKeys.all, 'c1-to-asr', 'calls', { ...params }] as const,
};

/**
 * Хук получения списка интеграций.
 */
export const useIntegrations = (params?: IntegrationListParams, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: integrationsKeys.list(params),
    queryFn: () => fetchIntegrations(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });

/**
 * Хук получения сведений об одной интеграции по коду.
 */
export const useIntegration = (code?: string) =>
  useQuery({
    queryKey: integrationsKeys.detail(code ?? ''),
    queryFn: () => fetchIntegration(code as string),
    enabled: !!code,
  });

/**
 * Хук получения списка звонков Bitrix24 за период.
 *
 * Запрос отправляется только когда задан хотя бы один из фильтров,
 * чтобы при первом открытии страницы не дёргать бэкенд впустую.
 */
export const useBitrixCalls = (params?: BitrixCallListParams) => {
  const hasAnyFilter = Boolean(
    params?.dateFrom?.from || params?.dateFrom?.to || params?.dateTo?.from || params?.dateTo?.to,
  );
  return useQuery({
    queryKey: integrationsKeys.bitrixCalls(params),
    queryFn: () => fetchBitrixCalls(params),
    enabled: hasAnyFilter,
    placeholderData: keepPreviousData,
  });
};

/**
 * Хук обновления сведений об интеграции.
 *
 * После успешного PUT инвалидируем оба кеша — список и детальную карточку,
 * чтобы одновременно обновились таблица «Все интеграции» и динамическое
 * подменю активных интеграций. Включённость анонимизатора входит и в готовность
 * страницы настройки анонимизации — её кеш инвалидируется вместе с ними.
 */
export const useUpdateIntegration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, payload }: { code: string; payload: IntegrationUpdateRequest }) =>
      updateIntegration(code, payload),
    onSuccess: (response) => {
      queryClient.setQueryData(integrationsKeys.detail(response.code), response);
      queryClient.invalidateQueries({ queryKey: integrationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: integrationsKeys.detail(response.code) });
      if (response.code === S1_ANONYMIZER_CODE) {
        queryClient.invalidateQueries({ queryKey: anonymizerConfigKeys.integrationReadiness() });
      }
    },
  });
};

/**
 * Хук переключения mock-режима интеграции.
 *
 * После успеха инвалидирует детальную карточку и все производные кеши
 * (статистика, журнал, сценарии), так как они зависят от текущего режима.
 */
export const useUpdateIntegrationMockMode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, payload }: { code: string; payload: IntegrationMockModeRequest }) =>
      updateIntegrationMockMode(code, payload),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: integrationsKeys.detail(variables.code) });
      queryClient.invalidateQueries({ queryKey: integrationsKeys.scenarios(variables.code) });
      queryClient.invalidateQueries({
        queryKey: [API_TAGS.INTEGRATIONS, 'statistics', variables.code],
      });
      queryClient.invalidateQueries({
        queryKey: [API_TAGS.INTEGRATIONS, 'request-log', variables.code],
      });
    },
  });
};

/**
 * Хук получения статистики запросов интеграции.
 */
export const useIntegrationStatistics = (code: string | undefined, params?: IntegrationStatisticsParams) =>
  useQuery({
    queryKey: integrationsKeys.statistics(code ?? '', params),
    queryFn: () => fetchIntegrationStatistics(code as string, params),
    enabled: !!code,
    placeholderData: keepPreviousData,
  });

/**
 * Хук получения журнала запросов интеграции с серверной пагинацией.
 */
export const useIntegrationRequestLog = (code: string | undefined, params?: IntegrationRequestLogParams) =>
  useQuery({
    queryKey: integrationsKeys.requestLog(code ?? '', params),
    queryFn: () => fetchIntegrationRequestLog(code as string, params),
    enabled: !!code,
    placeholderData: keepPreviousData,
  });

/**
 * Хук получения списка тестовых сценариев интеграции.
 */
export const useIntegrationScenarios = (code: string | undefined) =>
  useQuery({
    queryKey: integrationsKeys.scenarios(code ?? ''),
    queryFn: () => fetchIntegrationScenarios(code as string),
    enabled: !!code,
  });

/**
 * Хук запуска тестового сценария.
 *
 * После успешного запуска инвалидирует журнал запросов и статистику —
 * сценарий пишет результаты в `integration_request_log`.
 */
export const useRunIntegrationScenario = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      code,
      scenarioCode,
      payload,
    }: {
      code: string;
      scenarioCode: string;
      payload?: IntegrationScenarioRunRequest;
    }) => runIntegrationScenario(code, scenarioCode, payload),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: [API_TAGS.INTEGRATIONS, 'request-log', variables.code],
      });
      queryClient.invalidateQueries({
        queryKey: [API_TAGS.INTEGRATIONS, 'statistics', variables.code],
      });
    },
  });
};

/**
 * Хук получения журнала загрузок ASR-транскрибаций (c3-asr-demo).
 */
export const useC3AsrDemoLoads = (params?: C3AsrDemoLoadListParams) =>
  useQuery({
    queryKey: integrationsKeys.c3AsrDemoLoads(params),
    queryFn: () => fetchC3AsrDemoLoads(params),
    placeholderData: keepPreviousData,
  });

/**
 * Хук запуска загрузки ASR-транскрибаций из внешней очереди.
 *
 * После запуска инвалидирует журнал загрузок — пайплайн начинает наполнять
 * таблицу integration_c3_asr_demo_load по мере обработки строк.
 */
export const useStartC3AsrDemoLoad = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: startC3AsrDemoLoad,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [API_TAGS.INTEGRATIONS, 'c3-asr-demo', 'loads'],
      });
    },
  });
};

/**
 * Хук получения разговоров-кандидатов на загрузку по интервалу (c1-bot).
 *
 * Запрос уходит только когда переданы параметры (пользователь нажал «Показать
 * разговоры»), чтобы не тянуть источник при открытии карточки интеграции.
 */
export const useC1BotCandidates = (params?: C1BotCandidateListParams) =>
  useQuery({
    queryKey: integrationsKeys.c1BotCandidates(params),
    queryFn: () => fetchC1BotCandidates(params),
    enabled: params !== undefined,
    placeholderData: keepPreviousData,
  });

/**
 * Хук отбора звонков для отправки на транскрибацию (c1-to-asr).
 *
 * Запрос уходит только когда заданы все условия отбора: без них бэкенд
 * отвечает ошибкой, а пустая карточка не должна дёргать его впустую.
 */
export const useC1ToAsrCalls = (params?: C1ToAsrCallsParams) =>
  useQuery({
    queryKey: integrationsKeys.c1ToAsrCalls(params),
    queryFn: () => fetchC1ToAsrCalls(params as C1ToAsrCallsParams),
    enabled: Boolean(params?.dateFrom && params?.dateTo && params?.line),
    placeholderData: keepPreviousData,
  });

/**
 * Хук получения журнала прогрузки диалогов голосового бота (c1-bot).
 */
export const useC1BotLoads = (params?: C1BotLoadListParams) =>
  useQuery({
    queryKey: integrationsKeys.c1BotLoads(params),
    queryFn: () => fetchC1BotLoads(params),
    placeholderData: keepPreviousData,
  });

/**
 * Хук запуска прогрузки диалогов голосового бота.
 *
 * После запуска инвалидирует журнал загрузок — пайплайн наполняет его
 * по мере обработки разговоров источника.
 */
export const useStartC1BotLoad = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: startC1BotLoad,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [API_TAGS.INTEGRATIONS, 'c1-bot', 'loads'],
      });
    },
  });
};

/**
 * Хук отправки выбранных звонков на транскрибацию (c1-to-asr).
 *
 * После запуска инвалидирует отбор: у отправленных звонков появится
 * транскрипция, и повторный отбор должен показывать актуальное состояние.
 */
export const useStartC1ToAsrTranscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: startC1ToAsrTranscription,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [API_TAGS.INTEGRATIONS, 'c1-to-asr', 'calls'],
      });
    },
  });
};

/**
 * Хук настроек режима касания Bitrix24.
 */
export const useBitrixTouchSettings = () =>
  useQuery({
    queryKey: integrationsKeys.bitrixTouchSettings(),
    queryFn: fetchBitrixTouchSettings,
  });

/**
 * Хук сохранения настроек режима касания.
 *
 * Ответ сохранения — сами сохранённые настройки, поэтому он кладётся в кеш; перезапрос
 * оставлен на случай, когда режим сохраняли из другого места.
 */
export const useSaveBitrixTouchSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveBitrixTouchSettings,
    onSuccess: (settings) => {
      queryClient.setQueryData(integrationsKeys.bitrixTouchSettings(), settings);
      queryClient.invalidateQueries({ queryKey: integrationsKeys.bitrixTouchSettings() });
    },
  });
};

/**
 * Хук перечня заведённых касаний Bitrix24 с серверным листанием и отбором по исходу.
 */
export const useBitrixTouchSends = (params?: BitrixTouchSendListParams) =>
  useQuery({
    queryKey: integrationsKeys.bitrixTouchSends(params),
    queryFn: () => fetchBitrixTouchSends(params),
    placeholderData: keepPreviousData,
  });
